/**
 * KoshyThomasTrust — "Taught by Koshy Thomas" social-proof block.
 * Placed right after the hero on the landing page to immediately establish
 * credibility for Indian Plus One/Two commerce students.
 *
 * Design (Khadi & Indigo): a deep indigo (night-700) trust band with a faint
 * saffron jaali cross-hatch, a saffron-ringed headshot, credential chips, warm
 * trust copy, and the YouTube CTA. Premium and credible.
 */
export function KoshyThomasTrust() {
  return (
    <section
      aria-label="About the teacher"
      className="aces-band-night relative bg-night-700 overflow-hidden"
    >
      {/* Jaali cross-hatch signature — saffron on indigo, faded at edges */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage:
            'repeating-linear-gradient(45deg, rgba(224,123,26,0.10) 0 1px, transparent 1px 18px), repeating-linear-gradient(-45deg, rgba(224,123,26,0.10) 0 1px, transparent 1px 18px)',
          maskImage: 'radial-gradient(120% 80% at 50% 0%, #000 35%, transparent 80%)',
          WebkitMaskImage: 'radial-gradient(120% 80% at 50% 0%, #000 35%, transparent 80%)',
        }}
      />

      <div className="relative max-w-4xl mx-auto px-4 py-14 md:py-16">
        {/* Section label */}
        <div className="text-center mb-8">
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-accent-200">
            <span className="w-1.5 h-1.5 rounded-full bg-accent-500" aria-hidden="true" />
            Taught by a real teacher
          </span>
        </div>

        {/* Trust card */}
        <div className="relative rounded-[1.25rem] border border-white/10 bg-night-600/60 backdrop-blur-sm ring-1 ring-inset ring-white/5 shadow-card-raised overflow-hidden">
          {/* Subtle saffron top stripe */}
          <div
            aria-hidden="true"
            className="absolute inset-x-0 top-0 h-px"
            style={{ background: 'linear-gradient(90deg, transparent, rgba(224,123,26,0.55), transparent)' }}
          />

          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6 p-7 md:p-9">
            {/* Headshot */}
            <div className="flex-shrink-0">
              <div className="relative">
                {/* Saffron ring */}
                <div
                  aria-hidden="true"
                  className="absolute -inset-1 rounded-full"
                  style={{ background: 'radial-gradient(circle, rgba(224,123,26,0.55) 0%, rgba(224,123,26,0.12) 70%, transparent 100%)' }}
                />
                <img
                  src="/brand/koshy-thomas.webp"
                  alt="Koshy Thomas — commerce teacher"
                  width={96}
                  height={96}
                  loading="eager"
                  decoding="async"
                  className="relative w-24 h-24 rounded-full object-cover object-top ring-2 ring-accent-500 shadow-card"
                />
              </div>
            </div>

            {/* Text content */}
            <div className="flex-1 text-center sm:text-left">
              {/* Name + credential */}
              <div className="flex flex-col sm:flex-row sm:items-baseline gap-1.5 sm:gap-3 mb-3">
                <h2 className="font-serif text-2xl md:text-[1.65rem] text-ink-50 tracking-tight leading-tight">
                  Koshy Thomas
                </h2>
                <span className="inline-flex items-center self-center sm:self-auto gap-1.5 text-xs font-medium text-ink-100/80 bg-white/10 px-2.5 py-1 rounded-full border border-white/15">
                  Commerce teacher · Kerala · Plus One &amp; Plus Two
                </span>
              </div>

              {/* Credential chips */}
              <div className="flex flex-wrap gap-2 justify-center sm:justify-start mb-4">
                <CredChip
                  icon={
                    <svg viewBox="0 0 16 16" fill="currentColor" className="w-3.5 h-3.5" aria-hidden="true">
                      <path d="M8 .5C5.51.5 3.5 2.51 3.5 5c0 3.75 4.5 10.5 4.5 10.5S12.5 8.75 12.5 5C12.5 2.51 10.49.5 8 .5zm0 6.25a1.75 1.75 0 110-3.5 1.75 1.75 0 010 3.5z" />
                    </svg>
                  }
                  color="text-accent-200"
                  bg="bg-accent-500/15"
                  border="border-accent-500/30"
                  label="Kerala, India"
                />
                {/* YouTube subscriber badge */}
                <a
                  href="https://www.youtube.com/channel/UCh4KBkzwSbk-TEWZTi1maxw"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full bg-white/10 text-white border border-white/20 hover:bg-white/15 hover:border-white/30 transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400 focus-visible:ring-offset-1 focus-visible:ring-offset-night-700"
                  aria-label="14,000+ subscribers on YouTube — opens in new tab"
                >
                  <span className="text-red-500"><YouTubeIcon /></span>
                  14,000+ subscribers
                </a>
              </div>

              {/* Trust copy */}
              <p className="text-[15px] text-ink-100/90 leading-relaxed max-w-xl">
                Koshy Thomas, the Kerala commerce teacher behind the YouTube channel that thousands of students
                already learn from, is the public face of ACES. Chapter drafts are generated with AI assistance and
                checked in separate verification passes; the process is on our Sources page.
              </p>

              {/* CTA button */}
              <div className="mt-5">
                <a
                  href="https://www.youtube.com/channel/UCh4KBkzwSbk-TEWZTi1maxw"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 active:bg-red-800 px-4 py-2.5 rounded-lg shadow-card hover:shadow-card-raised transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 focus-visible:ring-offset-night-700"
                >
                  <YouTubeIcon className="w-4 h-4" />
                  Koshy Thomas Online Classes
                  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5 opacity-70" aria-hidden="true">
                    <path d="M3 8h10M9 4l4 4-4 4" />
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Internal helpers ──────────────────────────────────────────────────────────

function CredChip({
  icon,
  color,
  bg,
  border,
  label,
}: {
  icon: React.ReactNode;
  color: string;
  bg: string;
  border: string;
  label: string;
}) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border ${color} ${bg} ${border}`}
    >
      {icon}
      {label}
    </span>
  );
}

function YouTubeIcon({ className = 'w-3.5 h-3.5' }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
      aria-hidden="true"
    >
      <path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
    </svg>
  );
}
